import { useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Home, LayoutDashboard, FolderKanban, Boxes, Mail } from 'lucide-react'
import { getSessionId, useCurrentUser, needsPhone } from '../auth/currentUser.js'

/* =========================================================================
   ClientTabBar — the bottom tab bar a signed-in client gets on a phone.

   Rendered once, from App, so it follows the client across the marketing
   site and the portal rather than existing twice. It shows only when there
   is a session and only below lg, which is the same breakpoint at which the
   portal's own sidebar collapses into a drawer — so on a phone the client
   always has one-tap reach of the five places they actually go, instead of
   opening a drawer for every move.

   Deliberately not shown on the auth screens: a tab bar under a login form
   invites taps that would throw away a half-filled form.
   ========================================================================= */

const TABS = [
  { to: '/', label: 'Home', Icon: Home, end: true },
  { to: '/portal', label: 'Dashboard', Icon: LayoutDashboard, end: true },
  { to: '/portal/projects', label: 'Projects', Icon: FolderKanban },
  { to: '/portal/services', label: 'Services', Icon: Boxes },
  { to: '/contact', label: 'Contact', Icon: Mail },
]

/* Routes where the bar must stay out of the way. */
const HIDDEN_ON = [/^\/login/, /^\/signup/, /^\/forgot-password/, /^\/reset-password/]

export default function ClientTabBar() {
  const { pathname } = useLocation()
  /* Subscribed, not read once: without this the bar would keep its state from
     first render, so it would linger after a sign-out and stay missing after a
     sign-in until something else happened to re-render this component. */
  const { user } = useCurrentUser()
  const signedIn = Boolean(getSessionId() || user)
  const hidden = HIDDEN_ON.some((re) => re.test(pathname))
  /* An account that still owes us a number has no workspace to reach, so the
     shortcuts to it would be five taps that all land on the same wall. */
  const active = signedIn && !hidden && !needsPhone(user)

  /* While the bar is up it owns the bottom strip of the viewport, so the page
     needs to end above it and the WhatsApp bubble needs to sit higher. Both
     are done from one injected rule that is removed with the component —
     nothing to unwind by hand if the client signs out. */
  useEffect(() => {
    if (!active) return
    const style = document.createElement('style')
    style.setAttribute('data-client-tabbar', '')
    style.textContent = `
      @media (max-width: 1023.98px) {
        body { padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px)); }
        /* lift the floating WhatsApp bubble clear of the bar */
        .sx-wa-fab { bottom: calc(84px + env(safe-area-inset-bottom, 0px)) !important; }
      }
    `
    document.head.appendChild(style)
    return () => style.remove()
  }, [active])

  if (!active) return null

  return (
    <nav
      aria-label="Client shortcuts"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200/80 bg-white/95 backdrop-blur-xl lg:hidden"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxShadow: '0 -8px 24px -18px rgba(15,23,42,0.45)',
      }}
    >
      <ul className="mx-auto flex max-w-lg items-stretch">
        {TABS.map(({ to, label, Icon, end }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                `relative flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors ${
                  isActive ? 'text-blue-600' : 'text-slate-500 active:text-slate-700'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {/* the indicator sits on the bar's own top edge */}
                  <span
                    aria-hidden="true"
                    className={`absolute inset-x-4 top-0 h-[3px] rounded-b-full bg-blue-600 transition-opacity ${
                      isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <Icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2.4 : 1.9} />
                  <span className="leading-none">{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
